<!-- eslint-disable vuejs-accessibility/label-has-for -->
/* eslint-disable */
<template>
  <section class="ticket-conversation-view h-screen flex gap-4 p-4 bg-gray-50">
    <!-- Left Panel: Tickets List -->
    <div class="w-1/3 bg-white rounded-lg shadow-md flex flex-col overflow-hidden">
      <div class="p-4 border-b">
        <h2 class="text-xl font-bold mb-3">Tickets</h2>
        <div class="flex gap-2">
          <InputSearch
            class="rounded-md w-full"
            placeholder="Search tickets..."
            v-model:value="search"
            @input="handleSearch"
          />
          <Select
            v-model:value="statusFilter"
            class="min-w-[180px]"
            allowClear
            placeholder="Status"
            @change="handleStatusFilterChange"
          >
            <select-option v-for="status in statusOptions" :key="status" :value="status">
              {{ status }}
            </select-option>
          </Select>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto">
        <div v-for="ticket in filteredTickets" :key="ticket.id" role="button" tabindex="0" @click="selectTicket(ticket)"
          @keydown.enter.prevent="selectTicket(ticket)" @keydown.space.prevent="selectTicket(ticket)" :class="[
            'p-4 border-b cursor-pointer hover:bg-gray-100 transition',
            selectedTicket?.id === ticket.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
          ]">
          <div class="flex justify-between items-start mb-2">
            <span class="font-semibold text-sm">{{ ticket.ticket_number }}</span>
            <Tag :style="getStatusTagStyle(ticket.status)">{{ ticket.status }}</Tag>
          </div>
          <p class="text-sm text-gray-700 mb-1">{{ ticket.subject }}</p>
          <p class="text-xs text-gray-500">Owner: {{ ticket.owner_name }}</p>
          <p class="text-xs text-gray-400">{{ ticket.date }}</p>
        </div>
      </div>

      <div class="p-3 border-t">
        <Pagination
          :current="ticketsQuery?.data?.current_page || page"
          :total="ticketsQuery?.data?.total || 0"
          :page-size="ticketsQuery?.data?.per_page || perPage"
          show-less-items
          @change="handlePaginationChange"
        />
      </div>
    </div>

    <!-- Right Panel: Email Conversation -->
    <div class="w-2/3 bg-white rounded-lg shadow-md flex flex-col overflow-hidden">
      <div v-if="selectedTicket" class="flex flex-col h-full">
        <!-- Header -->
        <div class="p-4 border-b bg-gray-50">
          <h3 class="text-lg font-bold">{{ selectedTicket.ticket_number }}</h3>
          <p class="text-sm text-gray-600">{{ selectedTicket.subject }}</p>
          <div class="flex gap-4 mt-2 text-xs text-gray-500">
            <span>Owner: {{ selectedTicket.owner_name }}</span>
            <span class="flex items-center gap-1">Status: <Tag :style="getStatusTagStyle(selectedTicket.status)">{{ selectedTicket.status }}</Tag></span>
            <span>{{ selectedTicket.date }}</span>
          </div>
        </div>

        <!-- Email Thread -->
        <div class="flex-1 overflow-y-auto p-4 space-y-4" ref="conversationThread">
          <div v-for="email in selectedTicket.emails" :key="email.id" :class="[
            'p-3 rounded-lg max-w-md',
            email.sender === 'client'
              ? 'ml-auto bg-blue-100'
              : 'mr-auto bg-gray-100'
          ]">
            <p class="text-xs font-semibold mb-1">
              {{ email.sender_name }}
            </p>
            <p class="text-xs text-gray-500 mb-1">
              Subject: {{ email.subject }}
            </p>
            <p class="text-sm break-words" v-html="email.message"></p>

            <div v-if="email.attachments?.length" class="mt-2 space-y-2">
              <div v-for="att in email.attachments" :key="att._id || att.url" class="text-xs">
                <img
                  v-if="att.mime_type?.startsWith('image/')"
                  :src="att.url"
                  :alt="att.original_name || 'attachment'"
                  class="max-w-full rounded border"
                />
                <a
                  v-else
                  :href="att.url"
                  target="_blank"
                  rel="noopener"
                  class="text-blue-600 underline"
                >
                  {{ att.original_name || att.url }}
                </a>
              </div>
            </div>

            <p class="text-xs text-gray-500 mt-2">{{ email.timestamp }}</p>
          </div>
        </div>

        <!-- Reply Section -->
        <div class="p-4 border-t bg-gray-50">
          <div class="space-y-3">
            <textarea
              v-model="replyMessage"
              placeholder="Type your reply..."
              class="w-full p-3 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none"
              rows="4"
            />

            <div class="flex flex-wrap items-center gap-2">
              <input
                ref="replyAttachmentInput"
                id="reply-attachments"
                type="file"
                class="hidden"
                multiple
                @change="handleReplyAttachmentChange"
              />

              <Button class="rounded" @click="triggerReplyAttachmentPicker">
                Attach files
              </Button>

              <span class="text-xs text-gray-500">
                You can send text, attachments, or both.
              </span>
            </div>

            <div v-if="replyAttachments.length" class="space-y-2">
              <div
                v-for="(file, index) in replyAttachments"
                :key="`${file.name}-${file.size}-${index}`"
                class="flex items-center justify-between gap-3 rounded-md border bg-white px-3 py-2 text-xs"
              >
                <div class="min-w-0">
                  <p class="truncate font-medium text-gray-700">{{ file.name }}</p>
                  <p class="text-gray-400">{{ formatFileSize(file.size) }}</p>
                </div>

                <Button size="small" danger @click="removeReplyAttachment(index)">
                  Remove
                </Button>
              </div>
            </div>

            <div class="flex gap-2">
              <Button type="primary" class="btn-warning rounded" @click="confirmSendReply" :loading="replyLoading">
                Send Reply
              </Button>
              <Button type="primary" class="btn-success rounded" @click="changeStatusModal = true">
                Change Status
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="flex items-center justify-center h-full text-gray-400">
        <p>Select a ticket to view conversation</p>
      </div>
    </div>

    <!-- Change Status Modal -->
    <Modal v-model:visible="changeStatusModal" title="Change Ticket Status" :footer="null"
      @ok="changeStatusModal = false" @cancel="changeStatusModal = false">
      <div class="space-y-4">
        <div>
          <label id="status-select-label" for="status-select" class="block text-sm font-semibold mb-2">New Status</label>
          <Select id="status-select" aria-labelledby="status-select-label" v-model:value="newStatus" class="w-full" placeholder="Select status">
            <select-option v-for="status in statusOptions" :key="status" :value="status">
              {{ status }}
            </select-option>
          </Select>
        </div>

        <div v-if="newStatus === 'Resolved'">
          <Checkbox v-model:checked="sendResolutionEmail">
            Send email to notify the user
          </Checkbox>
        </div>

        <div class="flex gap-2 justify-end">
          <Button @click="changeStatusModal = false">Cancel</Button>
          <Button type="primary" class="btn-warning" @click="updateStatus" :loading="statusLoading">
            Update
          </Button>
        </div>
      </div>
    </Modal>

    <!-- query apis  -->
    <TicketsQuery
      :page="page"
      :per_page="perPage"
      :search="search"
      :search_status="searchStatusParam"
      :key="`${page}-${perPage}-${search}-${searchStatusParam}`"
    />
  </section>
</template>

<script>
/* eslint-disable */
import {
  InputSearch,
  Tag,
  Button,
  Modal,
  Select,
  message,
  Pagination,
  Checkbox,
} from 'ant-design-vue';
import TicketsQuery from '@/services/queries/admin/tickets/TicketsQuery.vue';
import ticketsApi from '@/services/apis/admin/tickets.api';

export default {
  components: {
    InputSearch,
    Tag,
    Button,
    Modal,
    Select,
    Pagination,
    Checkbox,
    TicketsQuery,
  },
  data() {
    return {
      search: '',
      selectedTicket: null,
      replyMessage: '',
      replyAttachments: [],
      replyLoading: false,
      changeStatusModal: false,
      newStatus: null,
      statusLoading: false,
      statusFilter: null,
      statusOptions: ['In Progress', 'Backlog', 'Resolved'],
      page: 1,
      perPage: 10,
      sendResolutionEmail: true,
      // Dummy tickets data
      tickets: [],
    };
  },
  computed: {
    ticketsQuery() {
      return this.$store.state.tickets.ticketsQuery;
    },
    ticketsFromQuery() {
      const rows = this?.ticketsQuery?.data?.data || [];
      return rows.map((d, ix) => ({
        id: d?._id || ix,
        ticket_number: d?.ticket_number,
        subject: d?.subject,
        owner_name: d?.user?.name,
        status: d?.status,
        date: d?.created_at?.split('T')[0],
        emails: d?.emails || [],
      }));
    },
    searchStatusParam() {
      return this.statusFilter || '';
    },
    filteredTickets() {
      return this.ticketsFromQuery;
    },
  },
  methods: {
    async selectTicket(ticket) {
      this.selectedTicket = { ...ticket, emails: [] };

      if (!ticket?.id) return;

      try {
        const resp = await ticketsApi.fetchTicketMessages(ticket.id);
        const rows = resp?.data || resp || [];

        this.selectedTicket.emails = rows.map((m, ix) => ({
          id: m?.id || ix,
          sender: m?.sender_type === 'client' ? 'client' : 'team',
          sender_name: m?.sender_type === 'client'
            ? (m?.from || ticket?.owner_name || 'User')
            : 'Commutrics Team',
          message: m?.body || '',
          subject: m?.subject || ticket?.subject || '',
          timestamp: m?.created_at_formatted || m?.received_at || '',
          attachments: m?.attachments || [],
        }));

        this.$nextTick(() => {
          this.scrollToConversationBottom();
        });
      } catch (e) {
        message.error('Failed to load ticket messages');
      }
    },
    scrollToConversationBottom() {
      const container = this.$refs.conversationThread;
      if (container && container.scrollHeight !== undefined) {
        container.scrollTop = container.scrollHeight;
      }
    },
    handleSearch(e) {
      this.search = e.target.value;
      this.page = 1;
    },
    handleStatusFilterChange() {
      this.page = 1;
    },
    handlePaginationChange(page) {
      this.page = page;
    },
    normalizeStatus(status) {
      return String(status || '')
        .trim()
        .toLowerCase()
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ');
    },
    getStatusTagStyle(status) {
      const styles = {
        new: {
          color: '#d46b08',
          backgroundColor: '#fff7e6',
          borderColor: '#ffd591',
        },
        'in progress': {
          color: '#d48806',
          backgroundColor: '#fffbe6',
          borderColor: '#ffe58f',
        },
        backlog: {
          color: '#cf1322',
          backgroundColor: '#fff1f0',
          borderColor: '#ffa39e',
        },
        resolved: {
          color: '#389e0d',
          backgroundColor: '#f6ffed',
          borderColor: '#b7eb8f',
        },
      };

      return styles[this.normalizeStatus(status)] || {
        color: '#595959',
        backgroundColor: '#fafafa',
        borderColor: '#d9d9d9',
      };
    },
    triggerReplyAttachmentPicker() {
      this.$refs.replyAttachmentInput?.click();
    },
    handleReplyAttachmentChange(e) {
      const files = Array.from(e?.target?.files || []);
      this.replyAttachments = [...this.replyAttachments, ...files];

      // Reset input so the same file can be selected again after removing it.
      if (e?.target) {
        e.target.value = '';
      }
    },
    removeReplyAttachment(indexToRemove) {
      this.replyAttachments = this.replyAttachments.filter((_, index) => index !== indexToRemove);
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B';

      const units = ['B', 'KB', 'MB', 'GB'];
      const sizeIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
      const size = bytes / Math.pow(1024, sizeIndex);

      return `${size.toFixed(sizeIndex === 0 ? 0 : 1)} ${units[sizeIndex]}`;
    },
    confirmSendReply() {
      const body = this.replyMessage.trim();

      if (!body && this.replyAttachments.length === 0) {
        message.warning('Please enter a message or attach at least one file');
        return;
      }
      if (!this.selectedTicket?.id) {
        message.error('Ticket not selected');
        return;
      }

      Modal.confirm({
        title: 'Send reply?',
        content: 'Are you sure you want to send this reply?',
        okText: 'Send',
        cancelText: 'Cancel',
        onOk: () => this.sendReply(),
      });
    },
    async sendReply() {
      const body = this.replyMessage.trim();

      if (!body && this.replyAttachments.length === 0) {
        message.warning('Please enter a message or attach at least one file');
        return;
      }
      if (!this.selectedTicket?.id) {
        message.error('Ticket not selected');
        return;
      }

      this.replyLoading = true;

      try {
        const formData = new FormData();
        formData.append('ticket_id', this.selectedTicket.id);
        formData.append('body', body);

        this.replyAttachments.forEach((file) => {
          formData.append('attachments[]', file);
        });
        await ticketsApi.replyToTicket(this.selectedTicket.id, formData);

        await this.selectTicket(this.selectedTicket);

        this.replyMessage = '';
        this.replyAttachments = [];
        message.success('Reply was sent successfully.');
      } catch (error) {
        message.error('Failed to prepare reply');
      } finally {
        this.replyLoading = false;
      }
    },
    async updateStatus() {
      if (!this.newStatus) {
        message.warning('Please select a status');
        return;
      }
      if (!this.selectedTicket?.id) {
        message.error('Ticket not selected');
        return;
      }

      this.statusLoading = true;
      try {
        await ticketsApi.changeTicketStatus({
          id: this.selectedTicket.id,
          status: this.newStatus,
          send_email: this.newStatus === 'Resolved' ? this.sendResolutionEmail : undefined,
        });
        message.success('Status updated successfully');

        this.selectedTicket.status = this.newStatus;
        this.changeStatusModal = false;
        this.newStatus = null;

        if (this.ticketsQuery) {
          await this.ticketsQuery.refetch();
        }
      } catch (error) {
        message.error('Failed to update status');
      } finally {
        this.statusLoading = false;
      }
    },
  },
  watch: {
    ticketsFromQuery(newTickets) {
      if (!this.selectedTicket && newTickets?.length) {
        this.selectTicket(newTickets[0]);
      }
      const stillExists = newTickets?.some((t) => t.id === this.selectedTicket?.id);
      if (this.selectedTicket && !stillExists && newTickets?.length) {
        this.selectTicket(newTickets[0]);
      }
    },
    newStatus(value) {
      if (value === 'Resolved') {
        this.sendResolutionEmail = true;
      }
    },
  },
  mounted() {
    if (this.tickets.length > 0) {
      this.selectTicket(this.tickets[0]);
    }
  },
};
</script>

<style scoped>
.ticket-conversation-view {
  background-color: #f5f5f5;
}
</style>
